import { DealerSpaceParameterKey } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import {
  defineSpaceParameterPanelValues,
  useSpaceParameterizedFilter,
} from '@src/components/AppLayout/SpaceParameters';
import {
  FilterClauseType,
  removeEmptyFilters,
  useCurrenciesFilter,
  type CustomerTransaction,
  type FilterClause,
  type FilterableProperty,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { compact, isArray, isEqual, keys } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import { useCounterpartiesFilter, useFeatureFlag } from '../../../hooks';
import type { CustomerBalancesFilter } from './types';

export interface UseCustomerBalancesFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export const CUSTOMER_BALANCES_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
  [DealerSpaceParameterKey.Currencies]: 'Currencies',
});
export const CUSTOMER_BALANCES_SPACE_PARAMETER_KEYS = CUSTOMER_BALANCES_SPACE_PARAMETER_SUPPORT.keys;

export function useCustomerBalancesFilter({ persistedBlotterTable }: UseCustomerBalancesFilterParams) {
  const { onFilterChanged: saveFilter } = persistedBlotterTable;
  const [localFilter, setFilter] = useState<CustomerBalancesFilter>(
    (persistedBlotterTable.initialState?.filter as CustomerBalancesFilter) || {}
  );

  const changeLocalFilter = useCallback(
    (action: SetStateAction<CustomerBalancesFilter>) => {
      const priorFilter = localFilter;
      const newFilter = action instanceof Function ? action(localFilter) : action;

      if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [localFilter, saveFilter]
  );
  const { changeFilter, filter, controlledKeys, getSelfManagedFilterBuilderProps } = useSpaceParameterizedFilter({
    filter: localFilter,
    changeFilter: changeLocalFilter,
    spaceParameterPanelValues: CUSTOMER_BALANCES_SPACE_PARAMETER_SUPPORT,
  });

  const currenciesFilter = useCurrenciesFilter();

  const counterpartiesFilter = useCounterpartiesFilter();
  const { requireCustomerBalancesCounterpartyFilter } = useFeatureFlag();

  const filterableProperties: FilterableProperty<keyof CustomerBalancesFilter>[] = useMemo(
    () => [currenciesFilter, { ...counterpartiesFilter, alwaysPresent: requireCustomerBalancesCounterpartyFilter }],
    [currenciesFilter, counterpartiesFilter, requireCustomerBalancesCounterpartyFilter]
  );

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<CustomerBalancesFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies CustomerBalancesFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const initialFilterClauses = useMemo(() => {
    type FilterKeys = keyof CustomerBalancesFilter;
    const filteredKeys = keys(filter) as FilterKeys[];
    const clauses: FilterClause<FilterKeys>[] = [];
    filteredKeys.forEach(key => {
      clauses.push({
        key: key,
        type: FilterClauseType.INCLUSIVE,
        selections: (isArray(filter[key]) ? filter[key] : compact([filter[key]])) as string[],
      });
    });
    return clauses;
  }, [filter]);

  const filterBuilderProps = useMemo(
    () =>
      getSelfManagedFilterBuilderProps({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }),
    [getSelfManagedFilterBuilderProps, initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );

  return {
    initialFilter: persistedBlotterTable.initialState?.filter,
    filter,
    changeFilter,
    controlledKeys,
    // shortcut to spread properties into useAccordionFilterBuilder.filterBuilderProps
    filterBuilderProps,
  };
}

const colIDToFilterBuilderKeyMap = {
  Counterparty: 'Counterparties',
  Currency: 'Currencies',
} satisfies Partial<Record<keyof CustomerTransaction, keyof CustomerBalancesFilter>>;
export function colIDToFilterBuilderKey(id: string): keyof CustomerBalancesFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap] as
    | keyof CustomerBalancesFilter
    | undefined;
}
